import '@radix-ui/react-dropdown-menu';

import { useContext } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { Tooltip } from '@/design-system';
import CheckIcon from '@/icons/CheckIcon.svg?react';
import DebugIcon from '@/icons/DebugIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { useGraphAssistantDebugGraph } from '../../api/useGraphSwr';
import { StateContext } from '../../control/state';
import { Node } from '../../data/misc';
import { useAssistantStore } from '../../store/assistants/assistantsStore';

export function GraphBreakpointDropdown() {
  const state = useContext(StateContext);
  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const graph = useGraphAssistantDebugGraph(assistantId);

  if (!graph.data) return null;
  const nodes = graph.data.nodes ?? [];
  const nodesWithBeforeInterrupts: string[] = [];
  const nodesWithAfterInterrupts: string[] = [];

  const nodesWithSubgraphs: Node[] = [
    ...nodes,
    ...Object.keys(graph.data.subgraphs).map((subgraph): Node => {
      const id = subgraph.split('|').join(':');
      return { id, data: id, metadata: {} };
    }),
  ];

  // Nodes with interrupts configured in code will be excluded from
  // having the same interrupt configured in the UI.
  nodesWithSubgraphs.forEach((node) => {
    const metadata = (node.metadata ?? {}) as { __interrupt?: string };
    const interrupts = (metadata.__interrupt ?? '').split(',');
    if (interrupts.includes('before')) {
      nodesWithBeforeInterrupts.push(node.id);
    }
    if (interrupts.includes('after')) {
      nodesWithAfterInterrupts.push(node.id);
    }
  });

  const availableNodes = new Set(
    nodesWithSubgraphs
      .filter(
        ({ id }) =>
          id != null &&
          id !== '__start__' &&
          id !== '__end__' &&
          !id.includes(':')
      )
      .map((i) => i.id)
  );

  const nUiBreakpoints =
    state.beforeBreakpoints.length + state.afterBreakpoints.length;
  const nTotalBreakpoints =
    nUiBreakpoints +
    nodesWithBeforeInterrupts.length +
    nodesWithAfterInterrupts.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-md border border-secondary px-2.5 py-1.5 text-sm outline-none transition-colors hover:bg-secondary-hover active:bg-secondary">
        <DebugIcon />
        <span>Interrupts</span>
        {nTotalBreakpoints > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full border border-secondary bg-secondary text-center text-xs text-tertiary">
            {nTotalBreakpoints}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {nodesWithSubgraphs
          .filter(
            ({ id }) =>
              availableNodes.has(id) ||
              nodesWithBeforeInterrupts.includes(id) ||
              nodesWithAfterInterrupts.includes(id)
          )
          .map((node) => {
            const beforeSelected = state.beforeBreakpoints.includes(node.id);
            const afterSelected = state.afterBreakpoints.includes(node.id);
            const beforeConfigured = nodesWithBeforeInterrupts.includes(
              node.id
            );
            const afterConfigured = nodesWithAfterInterrupts.includes(node.id);

            const isAvailable = availableNodes.has(node.id);

            return (
              <DropdownMenuItem
                key={node.id}
                className="flex justify-between gap-6 py-0 pb-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!isAvailable) return; // do nothing
                  if (!beforeConfigured) {
                    if (!beforeSelected || !afterSelected) {
                      state.addBeforeBreakpoint(node.id);
                    } else {
                      state.removeBeforeBreakpoint(node.id);
                    }
                  }

                  if (!afterConfigured) {
                    if (!afterSelected || !beforeSelected) {
                      state.addAfterBreakpoint(node.id);
                    } else {
                      state.removeAfterBreakpoint(node.id);
                    }
                  }
                }}
              >
                <span>{node.id}</span>

                <span
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Tooltip title={beforeConfigured && 'Interrupt set via code'}>
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 py-2 text-xs text-tertiary',
                        (!isAvailable || beforeConfigured) && 'opacity-50'
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!isAvailable || beforeConfigured) return; // do nothing
                        if (beforeSelected) {
                          state.removeBeforeBreakpoint(node.id);
                        } else {
                          state.addBeforeBreakpoint(node.id);
                        }
                      }}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-[4px] border border-secondary',
                          beforeSelected &&
                            'border-brand bg-brand-tertiary text-brand-green-400'
                        )}
                      >
                        {(beforeConfigured || beforeSelected) && (
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        )}
                      </span>
                      <span>Before</span>
                    </span>
                  </Tooltip>

                  <Tooltip title={afterConfigured && 'Interrupt set via code'}>
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 py-2 text-xs text-tertiary',
                        (!isAvailable || afterConfigured) && 'opacity-50'
                      )}
                      onClickCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!isAvailable || afterConfigured) return; // do nothing
                        if (afterSelected) {
                          state.removeAfterBreakpoint(node.id);
                        } else {
                          state.addAfterBreakpoint(node.id);
                        }
                      }}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-[4px] border border-secondary',
                          afterSelected &&
                            'border-brand bg-brand-tertiary text-brand-green-400'
                        )}
                      >
                        {(afterConfigured || afterSelected) && (
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        )}
                      </span>
                      <span>After</span>
                    </span>
                  </Tooltip>
                </span>
              </DropdownMenuItem>
            );
          })}
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClickCapture={(e) => {
            e.preventDefault();

            if (nUiBreakpoints === 0) {
              state.setBeforeBreakpoints(
                nodesWithSubgraphs
                  .map((i) => i.id)
                  .filter((id) => availableNodes.has(id))
                  .filter((id) => !nodesWithBeforeInterrupts.includes(id))
              );
              state.setAfterBreakpoints(
                nodesWithSubgraphs
                  .map((i) => i.id)
                  .filter((id) => availableNodes.has(id))
                  .filter((id) => !nodesWithAfterInterrupts.includes(id))
              );
            } else {
              state.clearBeforeBreakpoints();
              state.clearAfterBreakpoints();
            }
          }}
        >
          {nUiBreakpoints === 0 ? (
            <span>Interrupt on all</span>
          ) : (
            <span>Clear all</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
